// 戦闘・掃討の報酬。battleRewards は profile を書き換えずに計算し、applyRewards で反映する。
// 一度きりの報酬（星・図鑑・ボス初回・星箱）は once に source キー付きで返す。サーバーは grantOnce(source) が
// 新規だったときだけ applyOnceGrant を呼ぶ。
import { DEX_ENEMIES, ENEMIES, isDexEnemy } from '../data/enemies';
import type { RngHolder } from '../rng';
import { random } from '../rng';
import type { BattleResult, CharId, ChestLoot } from '../types';
import { CHAR_IDS } from '../types';
import { setKit } from './items';
import { companionIdOf } from './shikigami';
import { CONFIG } from './config';
import { countryOfStage } from './provinces';
import { addItem, rollEquipDrop, type EquipDrop } from './equipment';
import type { EquipItem, Rarity } from './profile';
import { gainExp } from './level';
import { bestChar, recommendedPower } from './loadout';
import { addOmamori, rollOmamori, type OmamoriRoll } from './omamori';
import { omikujiPerk } from './gacha';
import {
  addBundle,
  chapterOf,
  hasEntitlement,
  indexInChapter,
  isChapterBoss,
  stageKey,
  type Bundle,
  type OpResult,
  type Profile,
  type Resources,
  type ShikigamiState,
} from './profile';
import { isCastle } from './provinces';
import { rollRecruits, type RecruitRoll } from './shikigami';
import { addStamina, stageRewardWeight, stageStaminaCost } from './stamina';

export interface OnceGrant {
  /** grantOnce のキー。例 'star:12:3' 'star:12:3:d1' 'dex:e007' 'boss_first:10' 'starbox:2:30' 'dexbox:20' */
  source: string;
  bundle: Bundle;
  /** 生成済みのお守り（ランダム生成。applyOnceGrant が所持品に入れる） */
  omamori: OmamoriRoll[];
}

export interface Rewards {
  stageId: number;
  difficulty: number;
  win: boolean;
  stars: [boolean, boolean, boolean];
  /** 倍率込みのEXP */
  exp: number;
  /** 倍率を掛ける前のEXP（Lv100以降の霊玉換算用。撃破ボーナス込み） */
  baseExp: number;
  /** 撃破ボーナスの割合（0..killExpMax。基本EXPに掛かった分） */
  killBonus?: number;
  /** 主EXPの内訳（結果の画面で見せる）：base＝戦闘の基本（撃破ボーナス込み）、paid＝EXP2倍で増えた分、kishin＝寄進で増えた分、first＝初回クリアの追加 */
  expParts?: { base: number; paid: number; kishin: number; first: number };
  zeni: number;
  zaimoku: number;
  /** 霊玉（宝箱） */
  reidama?: number;
  /** お守り（戦闘のドロップと宝箱）。所持の上限を超えた分は applyRewards で御朱印になる */
  omamori?: OmamoriRoll[];
  /** 宝箱の中身（開けた順、量つき。結果画面用。銭・霊玉・装備は zeni/reidama/equipment に含む） */
  chests?: { loot: ChestLoot; zeni?: number; reidama?: number; equipIndex?: number }[];
  equipment: EquipDrop[];
  /** 勧誘の結果（recruited=true のものが仲間になった） */
  shikigami: RecruitRoll[];
  /** 勧誘後の式神の状態（天井カウンタ込み）。applyRewards がそのまま書き込む */
  shikigamiAfter: Record<string, ShikigamiState>;
  /** 図鑑に初めて載る敵 */
  dex: string[];
  /** 初めて取った星（0..2） */
  firstStars: number[];
  once: OnceGrant[];
  /** 未クリアのステージに勝ったときの兵糧の返却 */
  staminaRefund: number;
}

export interface BattleRewardsArgs {
  stage: number;
  difficulty: number;
  result: BattleResult;
  profile: Profile;
  firstClear: boolean;
  rng: RngHolder;
  /** EXPの倍率（EXP2倍・寄進の合計。例 2 * 1.2） */
  expMult: number;
  /** ドロップ抽選の回数の倍率（本日初出撃の3戦は2）。ドロップ2倍の権利（drop2x）は profile から見て更に掛ける */
  dropMult?: number;
  /** 式神の札を使っているか */
  fuda?: boolean;
  /** 出撃キャラ（武器ドロップの50%） */
  charId?: CharId;
  /** JST の日付（無料ガチャのボーナス：その日のドロップ・銭・材木の上乗せ） */
  day?: string;
}

export function stageBaseExp(stageId: number, difficulty = 0) {
  const r = CONFIG.rewards;
  return (
    stageRewardWeight(stageId, difficulty) *
    (r.expPerHyoro + r.expPerHyoroPerStage * stageId) *
    (isChapterBoss(stageId) ? r.expBossMult : 1) *
    (1 + r.difficultyBonus * difficulty) *
    chapterExpMult(stageId)
  );
}
/** 章ごとの1戦のEXPの倍率（rewards.chapterExpMult。終盤の章ほど実入りが大きい。BALANCE.md 17章） */
export function chapterExpMult(stageId: number) {
  return CONFIG.rewards.chapterExpMult[chapterOf(stageId) - 1] ?? 1;
}
/** 章ごとの銭・材木・装備のドロップ率の倍率（rewards.chapterLootMult） */
export function chapterLootMult(stageId: number) {
  return CONFIG.rewards.chapterLootMult[chapterOf(stageId) - 1] ?? 1;
}
/** 撃破ポイント（中立1・敵ヒーロー2・章ボス2・敵の建物1。CONFIG.rewards.killExpPoints） */
export function killPoints(result: BattleResult): number {
  const P = CONFIG.rewards.killExpPoints;
  let n = 0;
  for (const [id, count] of Object.entries(result.kills ?? {})) {
    const kind = ENEMIES[id]?.kind ?? 'neutral';
    n += (kind === 'prologue' ? 0 : (P[kind] ?? P.neutral)) * Math.max(0, count);
  }
  return n + P.building * Math.max(0, result.enemyBuildingsDestroyed ?? 0);
}
/** 撃破ボーナスの割合（基本EXPに掛ける。上限 killExpMax） */
export function killExpBonus(result: BattleResult): number {
  const r = CONFIG.rewards;
  return Math.min(r.killExpMax, Math.max(0, r.killExpPerPoint * killPoints(result)));
}

export function stageBaseZeni(stageId: number, difficulty = 0) {
  const r = CONFIG.rewards;
  return (r.zeniBase + r.zeniPerStage * stageId) * (1 + r.difficultyBonus * difficulty) * chapterLootMult(stageId);
}

export function starBundle(stageId: number, starIndex: number): Bundle {
  const t = CONFIG.rewards.star[starIndex] as { reidama: number; zaimoku: number; zaimokuPerChapter?: number };
  const b: Bundle = {};
  const rm = CONFIG.rewards.starReidamaChapterMult[chapterOf(stageId) - 1] ?? 1;
  if (t.reidama) b.reidama = Math.round(t.reidama * rm);
  const z = t.zaimoku + (t.zaimokuPerChapter ?? 0) * chapterOf(stageId);
  if (z) b.zaimoku = z;
  return b;
}

export function battleRewards(a: BattleRewardsArgs): Rewards {
  const r = CONFIG.rewards;
  const { stage, difficulty, result, profile, rng } = a;
  const win = result.win;
  const ch = chapterOf(stage);
  const key = stageKey(stage, difficulty);
  const prev = profile.progress[key];
  const lose = win ? 1 : r.loseRate;
  // 2回目以降の1戦の報酬の倍率（兵糧が1戦1・掃討チケットで戦闘数が増えた分。初回クリアは満額。GAME_DESIGN 20章）
  const rep = a.firstClear ? 1 : r.repeatMult;

  const firstBonus = win && a.firstClear ? Math.round((r.firstClearExpBase + r.firstClearExpPerStage * stage) * (r.firstClearExpChapterMult[chapterOf(stage) - 1] ?? 1)) : 0;
  const killBonus = killExpBonus(result);
  const battleExp = Math.round(stageBaseExp(stage, difficulty) * lose * rep * (1 + killBonus));
  const baseExp = battleExp + firstBonus;
  // EXP2倍・寄進の倍率は戦闘ぶんだけ（初回クリアの追加EXPは firstClearExpMult が true のときだけ掛ける）
  const exp = r.firstClearExpMult ? Math.round(baseExp * a.expMult) : Math.round(battleExp * a.expMult) + firstBonus;
  // 内訳：EXP2倍は基本ぶん（×(倍率-1)）、残りの上乗せが寄進
  const multBase = r.firstClearExpMult ? baseExp : battleExp;
  const paid = hasEntitlement(profile, 'exp2x') ? Math.min(exp - baseExp, Math.round(multBase * (CONFIG.paid.exp2xMult - 1))) : 0;
  const expParts = { base: battleExp, paid, kishin: Math.max(0, exp - baseExp - paid), first: firstBonus };
  // 無料ガチャのランクのボーナス（その日だけ）
  const perk = a.day ? omikujiPerk(profile, a.day) : null;
  const zeni = Math.round(stageBaseZeni(stage, difficulty) * lose * rep * (1 + (perk?.zeni ?? 0)));
  const forest = r.forestChapters.includes(ch) ? r.forestMult : 1;
  const zaimoku = Math.round(
    (r.zaimokuBase * (win ? 1 : 0) + result.enemyBuildingsDestroyed * (r.zaimokuPerBuilding + r.zaimokuPerChapter * ch)) * forest * rep * (1 + (perk?.zaimoku ?? 0)) * chapterLootMult(stage),
  );

  // 装備
  const equipment: EquipDrop[] = [];
  const omamori: OmamoriRoll[] = [];
  const enemyIds = Object.keys(result.kills).filter((id) => ENEMIES[id] && ENEMIES[id].kind !== 'rival' && ENEMIES[id].kind !== 'prologue');
  if (win) {
    const tier = Math.min(10, ch + difficulty);
    const charId = a.charId ?? 'akari';
    const boost = dropBoostMult(profile);
    const rolls = Math.max(1, Math.round(a.dropMult ?? 1)) * boost;
    // 極（ssr）は章ボスを上の難易度で倒したときの確定ドロップだけ
    const bossWeights = difficulty >= 1 && isChapterBoss(stage) ? r.bossHardRarityWeights : r.bossRarityWeights;
    for (let i = 0; i < rolls; i++) {
      // 期待値の個数だけ落とす（整数部分は確定、端数は確率）。終盤は章の倍率で1回に2つ以上も
      for (let n = dropCount(rng, r.dropChance * rep * chapterLootMult(stage) * (1 + (perk?.drop ?? 0))); n > 0; n--) equipment.push(rollEquipDrop(rng, tier, charId, enemyIds, undefined, false, difficulty));
      // ボスの確定ドロップは本日初出撃では増えず、ドロップ2倍の権利でだけ増える
      if (result.bossKilled && random(rng) < r.bossDropChance && i % Math.max(1, Math.round(a.dropMult ?? 1)) === 0)
        equipment.push(rollEquipDrop(rng, tier, charId, enemyIds, bossWeights, false, difficulty));
      // 出撃していないキャラの武器
      for (let n = dropCount(rng, r.benchWeaponChance * rep * chapterLootMult(stage) * (1 + (perk?.drop ?? 0))); n > 0; n--) equipment.push(rollEquipDrop(rng, tier, charId, enemyIds, undefined, true, difficulty));
      // お守り
      if (random(rng) < (result.bossKilled ? r.omamoriDrop.guardian : r.omamoriDrop.chance) * rep) omamori.push(rollOmamori(rng, { rarityWeights: r.omamoriDrop.rarityWeights as Record<Rarity, number> }));
    }
  }

  // 宝箱（戦闘中に開けた分。勝ち負けに関わらず）
  const C = r.chest;
  const chests: NonNullable<Rewards['chests']> = [];
  let chestZeni = 0;
  let chestReidama = 0;
  for (const loot of result.chests ?? []) {
    if (loot === 'zeni') {
      const z = Math.round(stageBaseZeni(stage, difficulty) * C.zeniMult * rep);
      chestZeni += z;
      chests.push({ loot, zeni: z });
    } else if (loot === 'reidama') {
      const n = C.reidama + Math.floor((ch - 1) / C.reidamaPerChapters);
      chestReidama += n;
      chests.push({ loot, reidama: n });
    } else if (loot === 'equip') {
      equipment.push(rollEquipDrop(rng, Math.min(10, ch + difficulty), a.charId ?? 'akari', enemyIds, undefined, false, difficulty));
      chests.push({ loot, equipIndex: equipment.length - 1 });
    } else if (loot === 'omamori') {
      omamori.push(rollOmamori(rng, { rarityWeights: r.omamoriDrop.rarityWeights as Record<Rarity, number> }));
      chests.push({ loot });
    }
  }

  // 式神（コピーの上で抽選）
  const tmp = { ...profile, shikigami: JSON.parse(JSON.stringify(profile.shikigami)) } as Profile;
  const rec = rollRecruits(tmp, rng, result.kills, { fuda: a.fuda });
  const shikigamiAfter: Record<string, ShikigamiState> = {};
  for (const id of Object.keys(result.kills)) if (tmp.shikigami[id]) shikigamiAfter[id] = tmp.shikigami[id];

  // 一度きり
  const once: OnceGrant[] = [];
  // 図鑑は大将（敵の操作キャラ）を除き、番人を含む（data/enemies.ts の DEX_LIST）
  const dex = Object.keys(result.kills).filter((id) => isDexEnemy(id) && !profile.dex.includes(id)).sort();
  for (const id of dex) once.push({ source: `dex:${id}`, bundle: { reidama: r.dexReidama }, omamori: [] });
  const dexAfter = profile.dex.length + dex.length;
  for (let n = r.dexOmamoriEvery; n <= dexAfter; n += r.dexOmamoriEvery)
    if (n > profile.dex.length) once.push({ source: `dexbox:${n}`, bundle: {}, omamori: [rollOmamori(rng)] });

  const firstStars: number[] = [];
  if (win) {
    for (let i = 0; i < 3; i++) {
      if (result.stars[i] && !prev?.stars[i]) {
        firstStars.push(i);
        once.push({ source: `star:${stage}:${i + 1}${difficulty ? `:d${difficulty}` : ''}`, bundle: starBundle(stage, i), omamori: [] });
      }
    }
    if (difficulty === 0 && firstStars.length) {
      const before = chapterStars(profile, ch);
      const after = before + firstStars.length;
      for (const box of r.chapterStarBox) {
        if (before < box.stars && after >= box.stars) {
          const b: Bundle = {};
          if (box.reidama) b.reidama = box.reidama;
          if (box.kuji) b.kuji = box.kuji;
          if (box.zaimoku) b.zaimoku = box.zaimoku;
          once.push({ source: `starbox:${ch}:${box.stars}`, bundle: b, omamori: box.omamori ? [rollOmamori(rng)] : [] });
        }
      }
    }
    if (a.firstClear && difficulty === 0 && isChapterBoss(stage))
      once.push({ source: `boss_first:${stage}`, bundle: { reidama: r.bossFirstReidama }, omamori: [rollOmamori(rng)] });
  }

  return {
    stageId: stage,
    difficulty,
    win,
    stars: win ? result.stars : [false, false, false],
    exp,
    baseExp,
    killBonus,
    expParts,
    zeni: zeni + rec.zeni + chestZeni,
    zaimoku,
    ...(chestReidama + rec.reidama ? { reidama: chestReidama + rec.reidama } : {}),
    ...(chests.length ? { chests } : {}),
    ...(omamori.length ? { omamori } : {}),
    equipment,
    shikigami: rec.rolls,
    shikigamiAfter,
    dex,
    firstStars,
    once,
    staminaRefund: win && a.firstClear && CONFIG.rewards.firstClearStaminaRefund ? stageStaminaCost(stage, difficulty) : 0,
  };
}

/** 期待値 e の個数：整数部分は確定、端数はその確率で1つ */
function dropCount(rng: RngHolder, e: number): number {
  const k = Math.floor(e);
  return k + (random(rng) < e - k ? 1 : 0);
}

/** ドロップ2倍（課金の永続権利 drop2x）の倍率 */
export const dropBoostMult = (p: Profile) => (hasEntitlement(p, 'drop2x') ? Math.max(1, Math.round(CONFIG.rewards.drop2xMult)) : 1);

function chapterStars(p: Profile, ch: number) {
  let n = 0;
  for (let i = 1; i <= 10; i++) n += p.progress[stageKey((ch - 1) * 10 + i)]?.stars.filter(Boolean).length ?? 0;
  return n;
}

export interface AppliedRewards {
  levelUps: number;
  overflowReidama: number;
  /** 売却した銭の合計（自動売却＋所持枠超過）。旧名のまま */
  dismantledZeni: number;
  /** 所持品に入った装備（追加効果つき） */
  items: EquipItem[];
  /** 自動売却（settings.autoSell）した数と銭 */
  autoSold: { count: number; zeni: number };
  /** 所持枠いっぱいで売った数と銭 */
  overflowSold: { count: number; zeni: number };
  /** お守り：所持品に入った id と、上限を超えて売った分の霊玉（goshuin は旧の御朱印。常に0） */
  omamori?: { ids: number[]; goshuin: number; reidama?: number };
  /** お供がいなかったので、初めて仲間になった式神を自動でお供にした（その敵ID） */
  companionAuto?: string;
}

/** 通常の報酬を反映（一度きりの報酬は含まない）。進行・星・図鑑・式神・装備・EXP・兵糧の返却 */
export function applyRewards(p: Profile, res: Resources, rw: Rewards, now: number, dayKey: string): AppliedRewards {
  const items: EquipItem[] = [];
  const autoSold = { count: 0, zeni: 0 };
  const overflowSold = { count: 0, zeni: 0 };
  for (const d of rw.equipment) {
    const r = addItem(p, d, { pickup: true });
    if (r.item) items.push(r.item);
    const box = r.sold === 'auto' ? autoSold : r.sold === 'overflow' ? overflowSold : null;
    if (box) {
      box.count++;
      box.zeni += r.zeni;
    }
  }
  const dismantledZeni = autoSold.zeni + overflowSold.zeni;
  res.zeni += rw.zeni + dismantledZeni;
  res.zaimoku += rw.zaimoku;
  if (rw.reidama) res.reidama += rw.reidama;
  // お守り（上限を超えた分は売値の霊玉に）
  const omamoriAdded: number[] = [];
  let omamoriGoshuin = 0;
  let omamoriReidama = 0;
  for (const o of rw.omamori ?? []) {
    const g = addOmamori(p, o);
    if (g.item) omamoriAdded.push(g.item.id);
    omamoriGoshuin += g.goshuin;
    omamoriReidama += g.reidama;
  }
  res.reidama += omamoriReidama;
  for (const [id, st] of Object.entries(rw.shikigamiAfter)) p.shikigami[id] = { ...st };
  // お供がいないキャラには、仲間になった式神を自動でお供にする（最初の1体を選び忘れて弱くならないように）。キャラごと
  let companionAuto: string | undefined;
  const first = rw.shikigami.find((r) => r.recruited && (p.shikigami[r.enemyId]?.star ?? 0) > 0);
  if (first)
    for (const c of CHAR_IDS)
      if (!companionIdOf(p, c)) {
        setKit(p, c, { companion: first.enemyId });
        if (c === p.oshi) companionAuto = first.enemyId;
      }
  for (const id of rw.dex) if (!p.dex.includes(id)) p.dex.push(id);
  if (rw.win) {
    const key = stageKey(rw.stageId, rw.difficulty);
    const pr = (p.progress[key] ??= { stars: [false, false, false], clears: 0 });
    pr.clears++;
    pr.stars = [0, 1, 2].map((i) => pr.stars[i] || rw.stars[i]) as [boolean, boolean, boolean];
  }
  if (rw.staminaRefund) addStamina(p, now, rw.staminaRefund);
  const g = gainExp(p, res, rw.exp, rw.baseExp, now, dayKey);
  return {
    levelUps: g.levelUps,
    overflowReidama: g.reidama,
    dismantledZeni,
    items,
    autoSold,
    overflowSold,
    ...(omamoriAdded.length || omamoriGoshuin || omamoriReidama ? { omamori: { ids: omamoriAdded, goshuin: omamoriGoshuin, ...(omamoriReidama ? { reidama: omamoriReidama } : {}) } } : {}),
    ...(companionAuto ? { companionAuto } : {}),
  };
}

/** 一度きりの報酬を反映（サーバーは grantOnce(source) が新規のときだけ呼ぶ） */
export function applyOnceGrant(p: Profile, res: Resources, g: OnceGrant) {
  addBundle(res, g.bundle);
  for (const o of g.omamori ?? []) {
    // 旧形式（主効果のIDだけ）の結果が残っていても受け取れるように
    const roll: OmamoriRoll = typeof o === 'string' ? { defId: o, rarity: 'r', q: 0.5, subs: [] } : o;
    res.reidama += addOmamori(p, roll).reidama;
  }
}

// ---- 掃討 ----

export function sweepFee(stageId: number, difficulty = 0): number {
  const s = CONFIG.sweep;
  return Math.round((s.zeniFeeBase + s.zeniFeePerStage * stageId) * (1 + difficulty));
}

/** 掃討1戦の費用：掃討チケット1枚（銭の手数料は今は0。兵糧は使わない。GAME_DESIGN 20章） */
export const sweepCost = (stageId: number, difficulty = 0) => ({ soutou: 1, zeni: sweepFee(stageId, difficulty) });

/** 掃討できるか：クリア済み、または（本城＝城攻め以外で）戦力が推奨の sweep.unclearedPowerRatio 倍以上。出撃できるか（isStageUnlocked）は呼び出し側で見る */
export function canSweep(p: Profile, stageId: number, difficulty = 0): OpResult<{ cleared: boolean }> {
  const cleared = (p.progress[stageKey(stageId, difficulty)]?.clears ?? 0) > 0;
  if (cleared) return { ok: true, cleared };
  if (isCastle(stageId)) return { ok: false, error: 'castle' };
  if (bestChar(p).power < CONFIG.sweep.unclearedPowerRatio * recommendedPower(stageId, difficulty)) return { ok: false, error: 'power' };
  return { ok: true, cleared };
}

// ---- 奉納（共同国盗り。銭の終盤の受け皿） ----

export function donate(p: Profile, res: Resources, zeni: number): OpResult<{ total: number }> {
  if (zeni < CONFIG.hounou.minZeni) return { ok: false, error: 'min' };
  if (res.zeni < zeni) return { ok: false, error: 'zeni' };
  res.zeni -= zeni;
  p.hounou += zeni;
  return { ok: true, total: p.hounou };
}

/** シミュレーター用：勝率モデルなどで作る仮の戦闘結果（ステージ内の敵を倒した扱い） */
export function syntheticResult(stageId: number, win: boolean, stars: [boolean, boolean, boolean], rng: RngHolder): BattleResult {
  const ch = chapterOf(stageId);
  const idx = indexInChapter(stageId);
  // エリートは章内4番目から野営地に出る（battle の stageDef と同じ）。7〜9番目は番人のエリートを勝てば倒している
  const pool = DEX_ENEMIES.filter((e) => e.chapter === ch && e.kind === 'neutral' && (e.role !== 'elite' || idx >= 4));
  const kills: Record<string, number> = {};
  const n = win ? 4 + Math.floor(random(rng) * 3) : 2;
  for (let i = 0; i < n; i++) {
    const e = pool[Math.floor(random(rng) * pool.length)];
    kills[e.id] = (kills[e.id] ?? 0) + 1;
  }
  const boss = isChapterBoss(stageId) && win;
  // 本城の番人（data/leaders.ts。入れ替えた章は e0x0 ではない）
  if (boss) kills[countryOfStage(stageId)?.guardian ?? `e${String(stageId).padStart(3, '0')}`] = 1;
  if (win && idx >= 7 && idx <= 9) {
    const elites = DEX_ENEMIES.filter((e) => e.chapter === ch && e.role === 'elite');
    const g = elites[(idx + 1) % elites.length];
    if (g) kills[g.id] = (kills[g.id] ?? 0) + 1;
  }
  // 宝箱：掃討は1つ開けた扱い（確率）
  const C = CONFIG.rewards.chest;
  const chests: ChestLoot[] = [];
  if (random(rng) < C.sweepChance) {
    const w = C.sweepWeights;
    const total = Object.values(w).reduce((s, x) => s + x, 0);
    let u = random(rng) * total;
    for (const [k, x] of Object.entries(w)) {
      if ((u -= x) < 0) {
        chests.push(k as ChestLoot);
        break;
      }
    }
  }
  return {
    win,
    reason: win ? 'core' : 'lost',
    chests,
    playerTurns: 18,
    ticks: 0,
    heroKOs: stars[1] ? 0 : 1,
    allRelaysOwned: stars[2],
    stars: win ? stars : [false, false, false],
    kills,
    bossKilled: boss,
    enemyBuildingsDestroyed: win ? 1 + Math.floor(random(rng) * 3) : 0,
    buildingsBuilt: 3,
  };
}
